import api from '../services/api';
import clientInputError from '../errors/clientInputError';

const paymentMethodsUrl = userId => `/user/${userId}/paymentMethods`;
const paymentMethodByTokenUrl = (userId, token) => `/user/${userId}/paymentMethods/${token}`;

/*
 * The only fields an update can change. PaymentMethodResource carries a dozen more - token, cardType,
 * last4 and friends - but BraintreePaymentGateway.updateCreditCardPaymentMethod reads just these five,
 * so anything else is accepted with a 200 and quietly dropped. Each is applied server side only when
 * non-blank, which is what makes a partial update safe: omitting a field leaves it as it was rather
 * than clearing it. isDefault is the exception, guarded on null rather than blank, so false is honored.
 */

export const UPDATABLE_FIELDS = Object.freeze([
    'cardholderName',
    'expirationDate',
    'postalCode',
    'deviceData',
    'isDefault',
]);

const paymentMethodService = {
    getAll,
    create,
    update,
    remove,
};

export default paymentMethodService;

/*
 * These endpoints are storefront-realm only and require the path userId to match the session user;
 * anything else is a 403 (see PaymentMethodRestServiceAdaptor). Always pass the current user's id.
 */

/*
 * A card Braintree declines does not arrive as an error. PaymentMethodProvider catches
 * BraintreeCreditCardVerificationException and returns
 * PaymentMethodResourceFactory.createResourceForVerificationError() - `{verificationError: true}` with
 * a 200 status - so both create and update have to inspect the body. That response deliberately
 * carries no card detail and no decline reason, so there is nothing more specific to hand back, and
 * echoing a gateway decline reason to the browser would be card-testing signal anyway.
 */

export function throwOnVerificationError (field, paymentMethod) {
    if (paymentMethod && paymentMethod.verificationError) {
        throw clientInputError([{
            field,
            message: 'This card could not be verified. Check the card details and try again.',
        }]);
    }

    return paymentMethod;
}

/*
 * Only the whitelisted fields are sent, so a caller cannot be misled into thinking a read-only field
 * like last4 took effect, and a field left out is genuinely left alone rather than sent as null.
 */

export function buildUpdatePayload (changes) {
    return UPDATABLE_FIELDS.reduce((payload, field) => {
        if (typeof changes[field] !== 'undefined') {
            payload[field] = changes[field];
        }

        return payload;
    }, {});
}

function getAll (userId) {
    const requestConfig = {
        url: paymentMethodsUrl(userId),
        method: 'GET',
    };

    return api(requestConfig);
}

/*
 * Vaulting happens server-side using Collaterate's own Braintree credentials, so a nonce obtained
 * with a client-safe tokenization key is sufficient here - no client token is required.
 *
 * Note the server sets makeDefault(true) unconditionally on create
 * (BraintreePaymentGateway.createNewPaymentMethodForUser), so every newly vaulted card becomes the
 * user's default whether or not they asked for that. There is no way to opt out over this endpoint.
 */

function create (userId, { nonce, cardholderName }) {
    const requestConfig = {
        url: paymentMethodsUrl(userId),
        method: 'POST',
        data: {
            nonce,
            cardholderName,
        },
    };

    return api(requestConfig).then(paymentMethod => throwOnVerificationError('nonce', paymentMethod));
}

/*
 * Every update runs a live Braintree card verification - the server sets verifyCard(true) on both
 * branches of updateCreditCardPaymentMethod - which is why this can fail on a card that merely sat in
 * the vault untouched. It also goes to Braintree's credit card API, so cards only: a PayPal token
 * reaches it as a missing card.
 */

function update (userId, token, changes) {
    const requestConfig = {
        url: paymentMethodByTokenUrl(userId, token),
        method: 'PATCH',
        data: buildUpdatePayload(changes),
    };

    return api(requestConfig).then(paymentMethod => throwOnVerificationError('token', paymentMethod));
}

function remove (userId, token) {
    const requestConfig = {
        url: paymentMethodByTokenUrl(userId, token),
        method: 'DELETE',
    };

    return api(requestConfig);
}
